import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import patternBackground from "../../../../../assets/patternBackground.png";
import conferenceBackground from "../../../../../assets/conference-pattern.png";
import { IStreamDetails, IThemeConfig } from "./types/memorial-types";
import { ReactComponent as Home } from "./icons/home.svg";
import { ReactComponent as User } from "./icons/user.svg";
import { formatDateToLongString } from "../../../../../utils/FormatDate";

interface EventHeaderProps {
  stream: IStreamDetails;
  theme: IThemeConfig;
}

const EventHeader: React.FC<EventHeaderProps> = ({ stream, theme }) => {
  const churchName = stream?.service?.church_name || "";
  const pastor = `${stream?.service?.leader_preacher_name || ""} ${
    stream?.service?.leader_preacher_surname || ""
  }`;

  const eventDate =
    stream?.service?.date_of_service ||
    stream?.booking?.events?.[0]?.start_date_of_event;

  const location =
    stream?.service?.venue?.name || stream?.booking?.events?.[0]?.venue?.name;

  const streamType = stream?.booking?.service;

  // Countdown State
  const [timeLeft, setTimeLeft] = useState({
    days: "000",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  // Countdown Logic
  useEffect(() => {
    if (!eventDate) return;

    const targetDate = new Date(eventDate).getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft({ days: "000", hours: "00", minutes: "00", seconds: "00" });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({
        days: String(days).padStart(3, "0"),
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [eventDate]);

  const background =
    streamType === "Conference" ? conferenceBackground : patternBackground;

  return (
    <Box
      sx={{
        width: "100%",
        height: streamType === "Conference" ? 240 : 200,
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: streamType === "Conference" ? "left" : "center",
        px: streamType === "Conference" ? 6 : 2,
      }}
    >
      {streamType === "Conference" ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            width: "100%",
            mt: { xs: 2 },
            mb: { xs: 3 },
          }}
        >
          {/* LEFT SIDE */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {stream?.booking?.events?.[0]?.title ||
                "Ulendo Leadership Breakfast"}
            </Typography>

            <Typography sx={{ fontSize: "1.2rem" }}>
              {formatDateToLongString(eventDate)}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                border: "1px solid rgba(255,255,255,0.4)",
                fontSize: "0.95rem",
                gap: 0.5,
                opacity: 0.9,
                width: "fit-content",
              }}
            >
              <Home style={{ width: 16, height: 16 }} />
              <Typography>
                {location || "Holiday Inn, Harare, Zimbabwe"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: { xs: 2, md: 0 },
              fontWeight: 600,
              fontSize: "1rem",
              textAlign: "center",
            }}
          >
            <Box>
              <Typography variant="h3">{timeLeft.days}</Typography>
              <Typography variant="caption">Days</Typography>
            </Box>
            <Box>
              <Typography variant="h3">{timeLeft.hours}</Typography>
              <Typography variant="caption">Hrs</Typography>
            </Box>
            <Box>
              <Typography variant="h3">{timeLeft.minutes}</Typography>
              <Typography variant="caption">Min</Typography>
            </Box>
            <Box>
              <Typography variant="h3">{timeLeft.seconds}</Typography>
              <Typography variant="caption">Sec</Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              fontFamily: "'Sacramento', cursive",
              fontSize: "2rem",
            }}
          >
            {churchName}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              fontSize: "1rem",
              opacity: 0.9,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Home style={{ width: 18, height: 18 }} />
                <Typography>{location}</Typography>
              </Box>
              {pastor && pastor.trim() !== "" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    mt: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <User style={{ width: 18, height: 18 }} />
                    <Typography>Pastor {pastor}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EventHeader;
